import React from "react";
import {
    BookOutlined,
    FileTextOutlined,
    RiseOutlined,
    SafetyCertificateOutlined,
    TrophyOutlined,
    UserOutlined,
} from "@ant-design/icons";
import {
    Resume,
    calculateAge,
    getGenderText,
} from "../../models/Resume";

interface ResumeDetailModalProps {
    resume: Resume;
}

export const ResumeDetailModal: React.FC<ResumeDetailModalProps> = ({ resume }) => {
    const educationHistory = resume.educationHistory ?? [];
    const workHistory = resume.workHistory ?? [];
    const papers = resume.papers ?? [];
    const certificates = resume.certificates ?? [];

    return (
        <div style={{ height: "85vh" }}>
            <div
                style={{
                    border: "8px solid #333",
                    borderRadius: "20px",
                    padding: "20px",
                    backgroundColor: "#f5f5f5",
                    maxWidth: "400px",
                    margin: "0 auto",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div style={{ flex: 1, overflowY: "auto", paddingRight: "8px" }}>
                    {resume.remoteWorkPhotoUrl && (
                        <div style={{ marginBottom: "20px" }}>
                            <img
                                src={resume.remoteWorkPhotoUrl}
                                alt="远程办公环境"
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: "8px",
                                    border: "1px solid #ddd",
                                }}
                            />
                        </div>
                    )}

                    <div style={{ textAlign: "center", marginBottom: "20px" }}>
                        <div style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "8px" }}>
                            {resume.name}
                        </div>
                        <div style={{ fontSize: "14px", color: "#666" }}>
                            {getGenderText(resume.gender)}
                            {resume.birthDate ? ` | ${calculateAge(resume.birthDate)}岁` : ""}
                            {resume.householdCity ? ` | ${resume.householdCity}` : ""}
                        </div>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                        <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px" }}>
                            <UserOutlined style={{ marginRight: "8px" }} />
                            联系方式
                        </div>
                        <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
                            <div>微信：{resume.wechatId}</div>
                            <div>邮箱：{resume.email}</div>
                            <div>手机：{resume.phone}</div>
                        </div>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                        <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px" }}>
                            <BookOutlined style={{ marginRight: "8px" }} />
                            教育背景
                        </div>
                        {educationHistory.map((edu, index) => (
                            <div
                                key={index}
                                style={{ fontSize: "14px", lineHeight: "1.6", marginBottom: "8px" }}
                            >
                                <div style={{ fontWeight: "bold" }}>{edu.schoolName}</div>
                                <div>
                                    {edu.major} | {edu.startDate} - {edu.endDate}
                                </div>
                                {edu.awards && <div style={{ color: "#666" }}>获奖：{edu.awards}</div>}
                            </div>
                        ))}
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                        <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px" }}>
                            <RiseOutlined style={{ marginRight: "8px" }} />
                            工作经历
                        </div>
                        {workHistory.map((work, index) => (
                            <div
                                key={index}
                                style={{ fontSize: "14px", lineHeight: "1.6", marginBottom: "8px" }}
                            >
                                <div style={{ fontWeight: "bold" }}>{work.companyName}</div>
                                <div>
                                    {work.position} | {work.startDate} - {work.endDate}
                                </div>
                                <div style={{ color: "#666" }}>工作内容：{work.workContent}</div>
                                {work.workAwards && (
                                    <div style={{ color: "#666" }}>获奖：{work.workAwards}</div>
                                )}
                                {work.skills && <div style={{ color: "#666" }}>技能：{work.skills}</div>}
                            </div>
                        ))}
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                        <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px" }}>
                            <TrophyOutlined style={{ marginRight: "8px" }} />
                            个人优势
                        </div>
                        <div style={{ fontSize: "14px", lineHeight: "1.6", color: "#666" }}>
                            {resume.personalAdvantage}
                        </div>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                        <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px" }}>
                            期望薪资
                        </div>
                        <div style={{ fontSize: "14px", color: "#666" }}>
                            {resume.expectedSalaryStart}K - {resume.expectedSalaryEnd}K
                        </div>
                    </div>

                    {papers.length > 0 && (
                        <div style={{ marginBottom: "20px" }}>
                            <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px" }}>
                                <FileTextOutlined style={{ marginRight: "8px" }} />
                                论文发表
                            </div>
                            {papers.map((paper, index) => (
                                <div
                                    key={index}
                                    style={{ fontSize: "14px", lineHeight: "1.6", marginBottom: "8px" }}
                                >
                                    <div style={{ fontWeight: "bold" }}>{paper.paperTitle}</div>
                                    <div style={{ color: "#666" }}>
                                        {paper.journalName} | {paper.isFirstAuthor ? "第一作者" : "合作作者"}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {certificates.length > 0 && (
                        <div style={{ marginBottom: "20px" }}>
                            <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px" }}>
                                <SafetyCertificateOutlined style={{ marginRight: "8px" }} />
                                资格证书
                            </div>
                            {certificates.map((cert, index) => (
                                <div
                                    key={index}
                                    style={{ fontSize: "14px", lineHeight: "1.6", marginBottom: "8px" }}
                                >
                                    <div>{cert.name}</div>
                                    {cert.imageUrl && (
                                        <img
                                            src={cert.imageUrl}
                                            alt={cert.name}
                                            style={{
                                                width: "100%",
                                                height: "auto",
                                                borderRadius: "8px",
                                                border: "1px solid #ddd",
                                                marginTop: "8px",
                                            }}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
